import { useEffect, useRef, useState } from 'react';
import type { Coordinates } from '../../types';
import { mapPositionToCoordinates } from '../../utils/geolocation';
import { requestCurrentPosition } from '../../utils/geolocationRequest';
import { haversineMetres } from '../transport/transportStopUtils';

/** ~1.5 km — refetch when user may have entered a neighbouring weather cell. */
const CELL_REFRESH_METRES = 1500;

export type ForecastLocationSource = 'live' | 'cached' | 'map';

interface LiveForecastLocation {
  lat: number;
  lng: number;
  source: ForecastLocationSource;
}

/**
 * Forecast anchor tied to the user's GPS cell — not map pan/zoom.
 * While weather view is open, watches position and refreshes only after meaningful movement.
 */
export function useLiveForecastLocation(
  enabled: boolean,
  userLocation: Coordinates | null,
  mapCenter: { lat: number; lng: number },
  setUserLocation: (location: Coordinates) => void,
): LiveForecastLocation {
  const [anchor, setAnchor] = useState<{ lat: number; lng: number } | null>(null);
  const [watchActive, setWatchActive] = useState(false);
  const watchIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setAnchor(null);
      setWatchActive(false);
      return;
    }
    if (userLocation) {
      setAnchor({ lat: userLocation.lat, lng: userLocation.lng });
    }
  }, [enabled, userLocation?.lat, userLocation?.lng]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;

    setWatchActive(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const next = mapPositionToCoordinates(position);
        setAnchor((prev) => {
          if (!prev) return { lat: next.lat, lng: next.lng };
          if (haversineMetres(prev.lat, prev.lng, next.lat, next.lng) >= CELL_REFRESH_METRES) {
            setUserLocation(next);
            return { lat: next.lat, lng: next.lng };
          }
          return prev;
        });
      },
      () => {
        // Keep last anchor / cached userLocation on watch errors.
      },
      {
        enableHighAccuracy: false,
        maximumAge: 60_000,
        timeout: 20_000,
      },
    );

    return () => {
      setWatchActive(false);
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled, setUserLocation]);

  if (anchor && watchActive) {
    return { lat: anchor.lat, lng: anchor.lng, source: 'live' };
  }
  if (anchor || userLocation) {
    const lat = anchor?.lat ?? userLocation!.lat;
    const lng = anchor?.lng ?? userLocation!.lng;
    return { lat, lng, source: 'cached' };
  }
  return { lat: mapCenter.lat, lng: mapCenter.lng, source: 'map' };
}

/** One-shot location refresh when opening weather view without a fix yet. */
export function useRequestLocationForWeather(
  enabled: boolean,
  userLocation: Coordinates | null,
  setUserLocation: (location: Coordinates) => void,
) {
  useEffect(() => {
    if (!enabled || userLocation) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;

    void requestCurrentPosition()
      .then((position) => setUserLocation(mapPositionToCoordinates(position)))
      .catch(() => {
        // LocationPermissionPrompt handles denied state elsewhere.
      });
  }, [enabled, userLocation, setUserLocation]);
}
